import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../services/supabaseClient';
import { validateSession, redirectToLogin } from '../../utils/sessionValidationUtil';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [checkingAdmin, setCheckingAdmin] = useState<boolean>(requireAdmin);
  const [sessionValid, setSessionValid] = useState<boolean>(false);

  // Validate session on mount - but don't block rendering
  useEffect(() => {
    const checkSession = async () => {
      if (loading) {
        // Wait for auth context to finish loading
        return;
      }

      if (!user) {
        // No user, let Navigate component handle redirect
        setSessionValid(false);
        return;
      }

      // If we have a user from AuthContext, trust it initially
      // This prevents blocking the UI while validating
      setSessionValid(true);

      try {
        // Validate session freshness in background
        const { valid, reason } = await validateSession();

        if (!valid) {
          console.log(`❌ Protected route: Session invalid - ${reason}`);
          // Redirect with current path for return after login
          redirectToLogin(reason, window.location.pathname);
          return;
        }

        console.log('✅ Protected route: Session validated');

      } catch (err) {
        console.error('Error validating session in ProtectedRoute:', err);
        // Don't immediately invalidate - let API calls handle auth errors
      }
    };

    checkSession();
  }, [user, loading]);

  // Check admin status if required
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !requireAdmin) {
        setCheckingAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        const isUserAdmin = data?.role === 'admin';
        setIsAdmin(isUserAdmin);
        console.log('Admin status check:', data?.role, 'Is admin:', isUserAdmin);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      } finally {
        setCheckingAdmin(false);
      }
    };

    if (requireAdmin && user && sessionValid) {
      checkAdminStatus();
    } else if (!requireAdmin) {
      // Only set checkingAdmin to false if admin is not required
      // If admin is required but sessionValid is not yet true, keep waiting
      setCheckingAdmin(false);
    } else if (requireAdmin && !user) {
      // No user, no need to check admin
      setCheckingAdmin(false);
    }
    // If requireAdmin && user && !sessionValid, keep checkingAdmin true and wait for sessionValid
  }, [user, requireAdmin, sessionValid]);

  // Show loading state only for auth context loading or admin check
  // Don't block on session validation - it happens in background
  if (loading || checkingAdmin) {
    return (
      <div className="d-flex justify-content-center align-items-center flex-column" style={{ minHeight: '300px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        {checkingAdmin && (
          <p className="mt-3 text-muted">Checking permissions...</p>
        )}
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If admin access is required but user is not an admin, redirect to home
  if (requireAdmin && !isAdmin) {
    console.log('Access denied: Admin privileges required');
    return <Navigate to="/" replace />;
  }

  // User is authenticated (and is admin if required), render the protected component
  return <>{children}</>;
};

export default ProtectedRoute;
